// src/pages/About.jsx
import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { styled } from '@mui/system';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import NoiseControlOffIcon from '@mui/icons-material/NoiseControlOff';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import HomeRepairServiceOutlinedIcon from '@mui/icons-material/HomeRepairServiceOutlined';
import constructionImage from '../assets/img/nosotros.jpg';

/* ================= Estilos premium ================= */
const Section = styled('section')({
  position: 'relative',
  background:
    'linear-gradient(180deg, #0f172a 0%, #0b1220 60%, #0a0f1c 100%)', // slate-900 style
  color: 'rgba(255,255,255,.92)',
  overflow: 'hidden',
});

const Banner = styled(Box)({
  position: 'relative',
  minHeight: 360,
  borderBottom: '1px solid rgba(255,255,255,.06)',
  display: 'grid',
  placeItems: 'center',
});

const BannerImage = styled('div')({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${constructionImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  filter: 'brightness(.65)',
});

const BannerOverlay = styled('div')({
  position: 'absolute',
  inset: 0,
  background:
    'radial-gradient(60% 60% at 50% 20%, rgba(0,0,0,.15), rgba(0,0,0,.55))',
});

const BannerContent = styled(Container)({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  paddingTop: 56,
  paddingBottom: 56,
});

const Title = styled(Typography)({
  fontWeight: 900,
  letterSpacing: '-.02em',
  textShadow: '0 2px 14px rgba(0,0,0,.35)',
});

const Subtitle = styled(Typography)({
  opacity: .95,
  maxWidth: 820,
  margin: '10px auto 0',
});

const BadgeRow = styled(Box)({
  display: 'flex',
  gap: 8,
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginTop: 18,
});

const Pill = styled(Chip)({
  color: '#111',
  fontWeight: 800,
  letterSpacing: '.04em',
  background: 'linear-gradient(180deg,#F3D462 0%,#DAA520 100%)',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,.35), 0 10px 24px rgba(218,165,32,.25)',
});

/* Tarjeta glass para bloques de texto */
const GlassCard = styled(Card)({
  height: '100%',
  borderRadius: 16,
  background: 'linear-gradient(180deg, rgba(255,255,255,.06) 0%, rgba(255,255,255,.03) 100%)',
  border: '1px solid rgba(255,255,255,.10)',
  boxShadow:
    '0 1px 0 rgba(255,255,255,.12) inset, 0 14px 26px rgba(0,0,0,.45), 0 60px 120px rgba(0,0,0,.35)',
  backdropFilter: 'blur(6px)',
  transition: 'transform .22s ease, box-shadow .28s ease, border-color .28s ease',
  '&:hover': {
    transform: 'translateY(-6px) scale(1.01)',
    boxShadow:
      '0 1px 0 rgba(255,255,255,.14) inset, 0 22px 42px rgba(0,0,0,.55), 0 100px 160px rgba(0,0,0,.38)',
    borderColor: 'rgba(218,165,32,.35)',
  },
});

const IconMedallon = styled('div')({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  marginBottom: 10,
  background:
    'linear-gradient(180deg, rgba(218,165,32,.22) 0%, rgba(218,165,32,.08) 100%)',
  border: '1px solid rgba(218,165,32,.45)',
  boxShadow: '0 10px 20px rgba(218,165,32,.28), inset 0 1px 0 rgba(255,255,255,.25)',
});

const BlockTitle = styled(Typography)({
  fontWeight: 900,
  letterSpacing: '.02em',
  color: '#F5DEB3',
});

const Body = styled(Typography)({
  color: 'rgba(255,255,255,.86)',
  letterSpacing: '0.02em',
  lineHeight: 1.7,
});

const Dot = styled('span')({
  display: 'inline-block',
  width: 5,
  height: 5,
  borderRadius: '50%',
  background: 'rgba(255,255,255,.28)',
  margin: '0 12px',
});

/* ================= Componente ================= */
const AboutUs = () => {
  return (
    <Section>
      {/* Hero / Banner */}
      <Banner>
        <BannerImage />
        <BannerOverlay />
        <BannerContent maxWidth="lg">
          <Title className="custom-title" variant="h2">
            Nosotros
          </Title>
          <Subtitle className="custom-second" variant="h6">
            <b className="custom-logo">FERRACORT</b>: calidad que se ve, confort que se siente.
          </Subtitle>
          <BadgeRow>
            <Pill icon={<WorkspacePremiumIcon />} label="30+ Años de trayectoria" />
            <Pill icon={<LocalShippingIcon />} label="Entrega rápida" />
            <Pill icon={<SupportAgentIcon />} label="Asesoramiento experto" />
          </BadgeRow>
        </BannerContent>
      </Banner>

      {/* Contenido */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={3} alignItems="stretch">
          {/* Bloque 1 */}
          <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <IconMedallon>
                  <HomeRepairServiceOutlinedIcon sx={{ fontSize: 32, color: '#EABE2a' }} />
                </IconMedallon>
                <BlockTitle className="custom-title" variant="h5" align="center" gutterBottom>
                  Sobre Nuestra Empresa
                </BlockTitle>
                <Subtitle variant="subtitle1" align="center" sx={{ mb: 2 }}>
                  Calidad, entrega rápida y asesoramiento experto
                </Subtitle>
                <Body className="Playfair Display" paragraph sx={{ textAlign: 'justify' }}>
                  En <b className="custom-logo">FERRACORT</b>, ofrecemos productos de alta calidad,
                  entrega rápida y asesoramiento experto. Con más de 30 años de experiencia como
                  empresa familiar, brindamos soluciones que mejoran la vida de nuestros clientes.
                </Body>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,.08)' }} />
                <Body className="custom-second" align="center">
                  Compromiso <Dot /> Honestidad <Dot /> Cercanía
                </Body>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Bloque 2 */}
          <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <IconMedallon>
                  <NoiseControlOffIcon sx={{ fontSize: 32, color: '#EABE2a' }} />
                </IconMedallon>
                <BlockTitle className="custom-title" variant="h5" align="center" gutterBottom>
                  Innovación y Eficiencia
                </BlockTitle>
                <Subtitle variant="subtitle1" align="center" sx={{ mb: 2 }}>
                  En cada producto y proceso
                </Subtitle>
                <Body className="Playfair Display" paragraph sx={{ textAlign: 'justify' }}>
                  Nuestras lamas de aluminio inyectadas destacan por su aislamiento térmico y acústico,
                  ofreciendo una barrera efectiva contra el frío, el calor y el ruido. Su fácil
                  mantenimiento asegura beneficios con mínimo esfuerzo.
                </Body>
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,.08)' }} />
                <Body className="custom-second" align="center">
                  Aislamiento térmico <Dot /> Ahorro energético <Dot /> Bajo mantenimiento
                </Body>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* Bloque 3 (full width) */}
          <Grid item xs={12}>
            <GlassCard>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <IconMedallon>
                  <ShieldOutlinedIcon sx={{ fontSize: 32, color: '#EABE2a' }} />
                </IconMedallon>
                <BlockTitle className="custom-title" variant="h5" align="center" gutterBottom>
                  Una Historia de Compromiso y Excelencia
                </BlockTitle>
                <Subtitle variant="subtitle1" align="center" sx={{ mb: 2 }}>
                  Tecnología + experiencia = confianza
                </Subtitle>
                <Body className="Playfair Display" paragraph sx={{ textAlign: 'justify', maxWidth: 980, mx: 'auto' }}>
                  Nuestra empresa nació de un sueño familiar para ofrecer soluciones duraderas. Con más de 30 años de
                  perfeccionamiento, nos mantenemos a la vanguardia tecnológica. La confianza de nuestros clientes es
                  nuestro mayor logro. Nos comprometemos a escuchar sus necesidades y ofrecer un servicio personalizado,
                  con expertos siempre disponibles para asesorar.
                </Body>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};

export default AboutUs;
